// AeroAiPanel — auto-observing coaching feed.
//
// Aero AI watches the rider's live data and pushes proactive observations.
// Two sources feed this panel:
//   - Track 3.1 coach event bus: the vision pipeline emits transition events
//     (baseline_locked, position_changed, new best, sustained steady state)
//     and useAeroCoachTriggers turns each one into a single-sentence beat.
//     This replaces the old fixed-interval poller.
//   - The user-typed chat path through useAeroAi (kept for free-form questions
//     even though no input is rendered today — useful for future Track 4.4
//     keyframe analysis to inject prompts).
//
// Each observation streams in as a timestamped card at the top of the feed.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import { useTranslation } from 'react-i18next';
import {
  AeroAiContext,
  AeroAiMessage,
  AeroAiSensors,
  useAeroAi,
} from '../hooks/useAeroAi';
import { useAeroCoachTriggers } from '../hooks/useAeroCoachTriggers';
import { useKeyframeCoach } from '../hooks/useKeyframeCoach';
import { CoachEventBus, CoachEventType } from '../vision/coachEvents';
import { Voice } from '../services/voice';
import { colors } from '../theme';

// Inert no-op bus so the trigger hook can render unconditionally even when
// the caller hasn't wired a real bus yet (e.g. transitional/legacy callers).
// We pass enabled=false in that case so it never actually sends.
const FALLBACK_BUS = new CoachEventBus();
// Stable empty ref so the keyframe hook can be created unconditionally;
// enabled flag below gates the actual WS open.
const FALLBACK_VIDEO_REF: React.RefObject<HTMLVideoElement> = { current: null };

interface Props {
  wsUrl: string;
  userId?: string;
  sessionId?: string;
  /** Returns the latest vision context to attach to each observation request. */
  getContext: () => AeroAiContext | undefined;
  getSensors?: () => AeroAiSensors | undefined;
  /** Gate observations until the user is actually in a trackable state. */
  ready: boolean;
  /** Current position class from the classifier (used to trigger on change). */
  positionClass: string | null;
  /** Track 3.1 — coach event bus from useVisionPipeline. When provided, the
   *  panel drives observations off pipeline events instead of a fixed timer. */
  coachBus?: CoachEventBus;
  /** Public vs Rapha — passed through to the trigger hook for voice tiering. */
  mode?: 'default' | 'rapha_event';
  locale?: string;
  /** Track 4.4 — video element to capture keyframes from for Claude vision. */
  videoRef?: React.RefObject<HTMLVideoElement>;
}

export default function AeroAiPanel({
  wsUrl,
  userId,
  sessionId,
  getContext,
  getSensors,
  ready,
  positionClass,
  coachBus,
  mode = 'default',
  locale,
  videoRef,
}: Props) {
  const { t } = useTranslation('session');
  const { observations, isStreaming, connected, error } = useAeroAi({
    wsUrl,
    userId,
    sessionId,
    getContext,
    getSensors,
  });

  // Track 3.1 — proactive observations emitted by the coach bus path. Kept
  // separate from the typed-chat observations so the WS streams don't mix.
  const [proactive, setProactive] = useState<
    Map<string, { id: string; content: string; streaming: boolean; trigger: CoachEventType; createdAt: number }>
  >(new Map());

  // Track 3.3 — voice coaching. One Voice instance per panel lifetime;
  // toggled via the localStorage `voiceEnabled` flag (default on). Each
  // completed proactive observation gets spoken once.
  const [voiceEnabled, setVoiceEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem('voiceEnabled') !== '0';
    } catch {
      return true;
    }
  });
  const voiceRef = useRef<Voice | null>(null);
  if (voiceRef.current == null) {
    voiceRef.current = new Voice({ wsUrl, userId, enabled: voiceEnabled });
  }
  useEffect(() => {
    voiceRef.current?.setEnabled(voiceEnabled);
    try {
      localStorage.setItem('voiceEnabled', voiceEnabled ? '1' : '0');
    } catch {}
  }, [voiceEnabled]);
  useEffect(() => {
    return () => {
      voiceRef.current?.stop();
    };
  }, []);
  // Track which proactive ids we've already spoken so re-renders don't
  // re-speak the same text.
  const spokenRef = useRef<Set<string>>(new Set());

  // Track 4.4 — keyframe vision analysis driven by position-change +
  // 30s steady-state ticks. Pipes its single-sentence output into the
  // same proactive observations map so it renders as just another card.
  useKeyframeCoach({
    wsUrl,
    userId,
    sessionId,
    videoRef: videoRef ?? FALLBACK_VIDEO_REF,
    positionClass,
    locale,
    enabled: !!videoRef && ready,
    onObservation: (msg) => {
      setProactive((prev) => {
        const next = new Map(prev);
        const existing = next.get(msg.id);
        next.set(msg.id, {
          id: msg.id,
          content: msg.content,
          streaming: msg.streaming,
          trigger: 'position_changed',
          createdAt: existing?.createdAt ?? Date.now(),
        });
        return next;
      });
      if (
        !msg.streaming &&
        msg.content.trim().length > 0 &&
        !spokenRef.current.has(msg.id)
      ) {
        spokenRef.current.add(msg.id);
        voiceRef.current?.speak({ text: msg.content, mode, locale, category: 'coach_beat' });
      }
    },
  });

  useAeroCoachTriggers({
    wsUrl,
    userId,
    sessionId,
    bus: coachBus ?? FALLBACK_BUS,
    getContext,
    getSensors,
    mode,
    locale,
    enabled: !!coachBus && ready,
    onObservation: (msg) => {
      setProactive((prev) => {
        const next = new Map(prev);
        const existing = next.get(msg.id);
        next.set(msg.id, {
          id: msg.id,
          content: msg.content,
          streaming: msg.streaming,
          trigger: msg.trigger,
          createdAt: existing?.createdAt ?? Date.now(),
        });
        return next;
      });
      // Speak once the observation finishes streaming. Skip empty bodies
      // (rare; happens if the engine returns no tokens).
      if (
        !msg.streaming &&
        msg.content.trim().length > 0 &&
        !spokenRef.current.has(msg.id)
      ) {
        spokenRef.current.add(msg.id);
        voiceRef.current?.speak({
          text: msg.content,
          mode,
          locale,
          trigger: msg.trigger,
          category: 'coach_beat',
        });
      }
    },
  });

  const proactiveAsMessages: AeroAiMessage[] = useMemo(() => {
    return Array.from(proactive.values())
      .sort((a, b) => a.createdAt - b.createdAt)
      .map((p) => ({
        id: p.id,
        role: 'assistant' as const,
        content: p.content,
        streaming: p.streaming,
        createdAt: p.createdAt,
      }));
  }, [proactive]);

  // Newest first; coach beats interleave with typed-chat observations by
  // timestamp so the feed reads in time order regardless of source.
  const merged = useMemo(() => {
    return [...observations, ...proactiveAsMessages].sort(
      (a, b) => a.createdAt - b.createdAt,
    );
  }, [observations, proactiveAsMessages]);
  const reversed = [...merged].reverse();
  void positionClass;

  return (
    <Box
      sx={{
        borderRadius: 3,
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            width: 34,
            height: 34,
            borderRadius: 1.5,
            background: colors.gradBrand,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 0 16px ${colors.brandShadow}`,
            position: 'relative',
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 18, color: colors.textInverse }} />
          {isStreaming && (
            <Box
              sx={{
                position: 'absolute',
                inset: -3,
                borderRadius: 1.75,
                border: `2px solid ${colors.brand}`,
                opacity: 0.6,
                animation: 'ring 1.6s ease-out infinite',
                '@keyframes ring': {
                  '0%': { transform: 'scale(1)', opacity: 0.7 },
                  '100%': { transform: 'scale(1.25)', opacity: 0 },
                },
              }}
            />
          )}
        </Box>
        <Box sx={{ flexGrow: 1 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.9375rem', lineHeight: 1.2 }}>
            {t('aeroAi.title')}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: ready && connected ? colors.brand : colors.textFaint,
                boxShadow: ready && connected ? `0 0 6px ${colors.brand}` : 'none',
              }}
            />
            <Typography
              sx={{
                fontSize: '0.625rem',
                fontWeight: 600,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: colors.textMuted,
              }}
            >
              {!connected
                ? t('aeroAi.connecting')
                : !ready
                  ? t('aeroAi.waitingForBaseline')
                  : isStreaming
                    ? t('aeroAi.observing')
                    : t('aeroAi.watching')}
            </Typography>
          </Box>
        </Box>
        <Tooltip title={t(voiceEnabled ? 'aeroAi.voiceOff' : 'aeroAi.voiceOn')}>
          <IconButton
            size="small"
            onClick={() => setVoiceEnabled((v) => !v)}
            sx={{ color: voiceEnabled ? colors.brand : colors.textMuted }}
          >
            {voiceEnabled ? (
              <VolumeUpIcon sx={{ fontSize: 18 }} />
            ) : (
              <VolumeOffIcon sx={{ fontSize: 18 }} />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Feed */}
      <Box
        sx={{
          p: 2,
          maxHeight: 480,
          overflowY: 'auto',
        }}
      >
        {error && (
          <Box
            sx={{
              mb: 1.5,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              backgroundColor: 'rgba(255, 71, 87, 0.1)',
              border: `1px solid rgba(255, 71, 87, 0.3)`,
            }}
          >
            <Typography variant="caption" sx={{ color: colors.danger }}>
              {error}
            </Typography>
          </Box>
        )}

        {reversed.length === 0 ? (
          <WaitingState ready={ready} connected={connected} />
        ) : (
          <Stack spacing={1.75}>
            {reversed.map((m, i) => (
              <ObservationCard key={m.id} message={m} isLatest={i === 0} />
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

function ObservationCard({
  message,
  isLatest,
}: {
  message: AeroAiMessage;
  isLatest: boolean;
}) {
  const { t } = useTranslation('session');
  const timeStr = new Date(message.createdAt).toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2,
        backgroundColor: isLatest ? 'rgba(106, 255, 69, 0.06)' : colors.bgSubtle,
        border: `1px solid ${isLatest ? colors.borderFocus : colors.border}`,
        transition: 'all 300ms',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          mb: 0.75,
        }}
      >
        {isLatest && (
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: colors.brand,
              boxShadow: `0 0 6px ${colors.brand}`,
            }}
          />
        )}
        <Typography
          sx={{
            fontSize: '0.625rem',
            fontWeight: 600,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: isLatest ? colors.brand : colors.textFaint,
            fontFamily: '"JetBrains Mono", monospace',
          }}
        >
          {isLatest ? t('aeroAi.now') : timeStr}
        </Typography>
      </Box>
      <Typography
        sx={{
          fontSize: '0.8125rem',
          lineHeight: 1.6,
          color: colors.text,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {message.content || (message.streaming && '…')}
        {message.content && message.streaming && (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              width: 6,
              height: 11,
              backgroundColor: colors.brand,
              ml: 0.25,
              verticalAlign: 'middle',
              animation: 'blink 1s infinite',
              '@keyframes blink': {
                '0%, 100%': { opacity: 1 },
                '50%': { opacity: 0.2 },
              },
            }}
          />
        )}
      </Typography>
    </Box>
  );
}

function WaitingState({
  ready,
  connected,
}: {
  ready: boolean;
  connected: boolean;
}) {
  const { t } = useTranslation('session');
  let message: string;
  if (!connected) {
    message = t('aeroAi.waitingConnecting');
  } else if (!ready) {
    message = t('aeroAi.waitingBaseline');
  } else {
    message = t('aeroAi.waitingReady');
  }

  return (
    <Box sx={{ py: 3, textAlign: 'center' }}>
      <Typography
        sx={{
          fontSize: '0.8125rem',
          color: colors.textMuted,
          lineHeight: 1.6,
        }}
      >
        {message}
      </Typography>
    </Box>
  );
}
